// TokenRefresher.ts
import type { KeycloakInstance } from "keycloak-js";
import debug from "debug";

const log = debug("app:login");

class TokenRefresher {
  private keycloak: KeycloakInstance;
  private intervalId: any = null;
  private refreshIntervalMs: number;
  private minValidity: number;
  private eventHub: any;
  private loginManager: any;

  constructor(
    keycloak: KeycloakInstance,
    eventHub?: any,
    loginManager?: any,
    refreshIntervalMs = 60000, // ⏱️ Every 60 seconds
    minValidity = 30            // 🔐 Refresh if token expires in <30s
  ) {
    this.keycloak = keycloak;
    this.eventHub = eventHub;
    this.loginManager = loginManager;
    this.refreshIntervalMs = refreshIntervalMs;
    this.minValidity = minValidity;
  }

  start() {
    this.stop(); // prevent duplicates

    this.intervalId = setInterval(async () => {
      try {
        const refreshed = await this.keycloak.updateToken(this.minValidity);
        if (refreshed) {
          log("🔄 Token refreshed automatically");
        }
      } catch (err: any) {
        log("⚠️ Failed to refresh token automatically", err);

        // Check if session expired.
        // Keycloak sometimes surfaces refresh failures as a generic HTTP error (e.g. 400)
        // with minimal err.error/err.error_description. Treat those as expired too.
        const rawMessage =
          (err?.error_description ||
            err?.error ||
            err?.message ||
            String(err || "")).toLowerCase();

        const httpStatus = err?.status || err?.httpCode || err?.statusCode;

        const isSessionExpired =
          err?.error === "invalid_grant" ||
          err?.error === "invalid_token" ||
          rawMessage.includes("token expired") ||
          rawMessage.includes("session expired") ||
          rawMessage.includes("refresh token") ||
          rawMessage.includes("invalid_grant") ||
          rawMessage.includes("invalid_token") ||
          httpStatus === 400 ||
          httpStatus === 401;

        if (isSessionExpired && this.eventHub) {
          // Emit session expired event instead of direct call
          this.eventHub.emit("sessionExpired");
        }
      }
    }, this.refreshIntervalMs);
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      log("⏹️ Token refresher stopped");
    }
  }
}

export default TokenRefresher;
